import { useState, useMemo } from 'react';
import { FilterMatchMode } from 'primereact/api';
import { useGetVehicleWarrantyClaimsReportSumQuery } from '../../../../api/endpoints/vehicleWarrantyClaimsReportSum';
import { matchModeToOperator } from '../../../../utils/filterUtils';

const backendColumnMap = {
    claimNo: 'ClaimNo',
    chassisNo: 'ChassisNo',
    dealerName: 'DealerName',
    countryName: 'CountryName',
    gtNumber: 'GtNumber',
    dealerCurrency: 'DealerCurrency',
    claimTotalAmount: 'ClaimTotalAmount',
    customerPaidTotalAmount: 'CustomerPaidTotalAmount',
    warrantyPartTotal: 'WarrantyPartTotal',
    warrantyLaborTotal: 'WarrantyLaborTotal',
    warrantyOtherCostTotal: 'WarrantyOtherCostTotal',
    towingTotal: 'TowingTotal',
    roadsideAssistanceTotal: 'RoadsideAssistanceTotal',
    otherLaborTotal: 'OtherLaborTotal',
    otherPartTotal: 'OtherPartTotal'
};

export const FILTER_FIELDS = Object.keys(backendColumnMap);

export const AMOUNT_FIELDS = new Set(['claimTotalAmount', 'customerPaidTotalAmount', 'warrantyPartTotal', 'warrantyLaborTotal', 'warrantyOtherCostTotal', 'towingTotal', 'roadsideAssistanceTotal', 'otherLaborTotal', 'otherPartTotal']);

const normalizeAmountValue = (rawValue) => rawValue.replace(/\s/g, '').replace(/\./g, '').replace(',', '.');

const INITIAL_FILTER_STATE = FILTER_FIELDS.reduce((acc, field) => {
    acc[field] = null;
    acc[`${field}MatchMode`] = null;
    return acc;
}, {});

const INITIAL_LAZY_PARAMS = { first: 0, rows: 10, page: 1 };

const getBackendColumnName = (field) => backendColumnMap[field] || null;

const getOperatorValue = (matchMode) => matchModeToOperator[matchMode] ?? matchModeToOperator[FilterMatchMode.CONTAINS];

const buildDynamicFilters = (filtersState) => {
    const filters = [];

    FILTER_FIELDS.forEach((field) => {
        const column = getBackendColumnName(field);
        if (!column) return;

        const value = filtersState?.[field];
        if (value === null || value === undefined || value === '') return;

        const trimmed = String(value).trim();
        if (!trimmed) return;

        const filterValue = AMOUNT_FIELDS.has(field) ? normalizeAmountValue(trimmed) : trimmed;
        if (!filterValue) return;

        filters.push({ column, operator: getOperatorValue(filtersState?.[`${field}MatchMode`]), value: filterValue });
    });

    return filters;
};

const buildDynamicSorts = (field, order) => {
    if (!field || order === 0) return null;
    const column = getBackendColumnName(field);
    if (!column) return null;
    return [{ column, direction: order === 1 ? 1 : 2 }];
};

const STORAGE_KEY = 'vehicleWarrantyClaimsReportSumFilters';

const loadFiltersFromStorage = () => {
    try {
        const saved = localStorage.getItem(STORAGE_KEY);
        return saved ? JSON.parse(saved) : null;
    } catch {
        return null;
    }
};

const saveFiltersToStorage = (filters, sortField, sortOrder, lazyParams) => {
    try {
        localStorage.setItem(STORAGE_KEY, JSON.stringify({ filters, sortField, sortOrder, lazyParams }));
    } catch {
        return;
    }
};

const useVehicleWarrantyClaimsReportSum = () => {
    const initialStoredData = loadFiltersFromStorage();

    const [lazyParams, setLazyParams] = useState(() => initialStoredData?.lazyParams || INITIAL_LAZY_PARAMS);
    const [sortField, setSortField] = useState(() => initialStoredData?.sortField || null);
    const [sortOrder, setSortOrder] = useState(() => initialStoredData?.sortOrder || 0);
    const [activeFilters, setActiveFilters] = useState(() => initialStoredData?.filters || INITIAL_FILTER_STATE);

    const queryParams = useMemo(() => {
        const params = {
            pageNumber: lazyParams.page,
            pageSize: lazyParams.rows
        };
        const filters = buildDynamicFilters(activeFilters);
        if (filters.length > 0) params.filters = filters;
        const sorts = buildDynamicSorts(sortField, sortOrder);
        if (sorts) params.sorts = sorts;
        return params;
    }, [activeFilters, lazyParams.page, lazyParams.rows, sortField, sortOrder]);

    const exportParams = useMemo(() => {
        const params = { compressionType: 0 };
        const filters = buildDynamicFilters(activeFilters);
        if (filters.length > 0) params.filters = filters;
        const sorts = buildDynamicSorts(sortField, sortOrder);
        if (sorts) params.sorts = sorts;
        return params;
    }, [activeFilters, sortField, sortOrder]);

    const { data: apiData, isLoading, isFetching } = useGetVehicleWarrantyClaimsReportSumQuery(queryParams);

    const handleFilterChange = (updatedFilters) => {
        setActiveFilters(updatedFilters);
        const newLazyParams = { first: 0, rows: lazyParams.rows, page: 1 };
        setLazyParams(newLazyParams);
        saveFiltersToStorage(updatedFilters, sortField, sortOrder, newLazyParams);
    };

    const initFilters = () => {
        setActiveFilters(INITIAL_FILTER_STATE);
        setSortField(null);
        setSortOrder(0);
        const newLazyParams = { first: 0, rows: lazyParams.rows, page: 1 };
        setLazyParams(newLazyParams);
        saveFiltersToStorage(INITIAL_FILTER_STATE, null, 0, newLazyParams);
    };

    const onPage = (event) => {
        const newLazyParams = { first: event.first, rows: event.rows, page: event.page + 1 };
        setLazyParams(newLazyParams);
        saveFiltersToStorage(activeFilters, sortField, sortOrder, newLazyParams);
    };

    const handleSortChange = (event) => {
        const nextSortField = event?.sortField || null;
        const nextSortOrder = event?.sortOrder || 0;
        setSortField(nextSortField);
        setSortOrder(nextSortOrder);
        const newLazyParams = { first: 0, rows: lazyParams.rows, page: 1 };
        setLazyParams(newLazyParams);
        saveFiltersToStorage(activeFilters, nextSortField, nextSortOrder, newLazyParams);
    };

    return {
        apiData,
        isLoading: isLoading || isFetching,
        lazyParams,
        sortField,
        sortOrder,
        activeFilters,
        exportParams,
        onPage,
        onSort: handleSortChange,
        handleFilterChange,
        initFilters
    };
};

export default useVehicleWarrantyClaimsReportSum;
